import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Photo from "../components/ui/Photo";
import {
  bySlug,
  categoryLabel,
  categoryLatin,
  relatedServices,
  staffFor,
} from "../data/salon";
import { duration, faPercent, finalPrice, toman } from "../lib/format";

import { useBooking } from "../lib/booking";
import ServiceCard from "../components/ui/ServiceCard";
import { Stars } from "../components/ui/Bits";
import NotFound from "./NotFound";
import { useDocumentTitle } from "../lib/title";
import "./pages.css";

const PANELS = [
  { slug: "includes", label: "این جلسه شامل چیست" },
  { slug: "aftercare", label: "مراقبت بعد از جلسه" },
  { slug: "policy", label: "شرایط رزرو و لغو" },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = bySlug(slug);

  // called before the NotFound bail-out below, so the hook order stays stable
  useDocumentTitle(service ? service.name : "صفحه پیدا نشد");

  const [staffId, setStaffId] = useState(null);
  const [panel, setPanel] = useState("includes");
  const booking = useBooking();

  if (!service) return <NotFound />;

  const team = staffFor(service.category);
  const added = booking.has(service.id);

  return (
    <>
      <nav className="crumb wrap wrap--wide" aria-label="مسیر">
        <Link to="/">خانه</Link>
        <span aria-hidden="true">/</span>
        <Link to="/services">خدمات</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{service.name}</span>
      </nav>

      <section
        className="sdetail"
        style={{ "--tone": service.tone, "--tone2": service.tone2 }}
      >
        <div className="wrap wrap--wide sdetail__grid">
          {/* ------------------------------------------------ 3d stage */}
          <div className="sdetail__stage">
            <Photo src={service.photo} alt={service.name} ratio="4 / 5" scrub priority />
          </div>

          {/* ---------------------------------------------------- info */}
          <div className="sdetail__info">
            <span className="eyebrow">{categoryLatin(service.category)}</span>
            <span className="sdetail__cat">{categoryLabel(service.category)}</span>
            <h1 className="sdetail__title">{service.name}</h1>

            <div className="sdetail__rating">
              <Stars rating={service.rating} reviews={service.reviews} />
            </div>

            <p className="sdetail__lede">{service.description}</p>

            <div className="sdetail__facts">
              <div>
                <span>قیمت</span>
                <strong>
                  {toman(finalPrice(service))}
                  {service.discount > 0 && (
                    <em className="sdetail__off">{faPercent(service.discount)} تخفیف</em>
                  )}
                </strong>
              </div>
              <div>
                <span>مدت جلسه</span>
                <strong>{duration(service.duration)}</strong>
              </div>
            </div>

            {team.length > 0 && (
              <div className="sdetail__team">
                <span className="sdetail__teamLabel">انتخاب متخصص</span>
                <div className="sdetail__chips">
                  <button
                    type="button"
                    className={`schip${staffId === null ? " is-on" : ""}`}
                    onClick={() => setStaffId(null)}
                  >
                    فرقی ندارد
                  </button>
                  {team.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`schip${staffId === m.id ? " is-on" : ""}`}
                      onClick={() => setStaffId(m.id)}
                      style={{ "--chip": m.tone }}
                    >
                      <span className="schip__dot" aria-hidden="true" />
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="sdetail__actions">
              <button
                type="button"
                className="btn"
                onClick={() => (added ? booking.setOpen(true) : booking.add(service, staffId))}
              >
                {added ? "در سبد نوبت است — مشاهده" : "افزودن به سبد نوبت"}
              </button>
              <Link to="/booking" className="btn btn--ghost">رزرو مستقیم</Link>
            </div>

            {/* --------------------------------------------- accordion */}
            <div className="acc">
              {PANELS.map((p) => (
                <div key={p.slug} className={`acc__item${panel === p.slug ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className="acc__head"
                    aria-expanded={panel === p.slug}
                    onClick={() => setPanel(panel === p.slug ? null : p.slug)}
                  >
                    {p.label}
                    <span className="acc__sign" aria-hidden="true" />
                  </button>

                  <div className="acc__body">
                    <div className="acc__inner">
                      {p.slug === "includes" && (
                        <ul className="acc__list">
                          {service.includes.map((x) => <li key={x}>{x}</li>)}
                        </ul>
                      )}
                      {p.slug === "aftercare" && <p>{service.aftercare}</p>}
                      {p.slug === "policy" && (
                        <p>
                          لغو یا جابه‌جایی نوبت تا ۲۴ ساعت قبل رایگان است. برای
                          پکیج عروس، جلسه تست جداگانه هماهنگ می‌شود.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section--ivory">
        <div className="wrap wrap--wide">
          <h2 className="sec-head__title" style={{ marginBottom: "var(--s-7)" }}>
            خدمات مرتبط
          </h2>
          <div className="plist__grid">
            {relatedServices(service).map((s) => (
              <ServiceCard key={s.id} service={s} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
